import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Product } from "@shared/types";
import { api } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useWishlist } from "@/lib/wishlist";
import { formatMoney } from "@/lib/format";
import { Button } from "@/components/Button";
import { ChevronLeftIcon, HeartIcon, MinusIcon, PlusIcon } from "@/components/icons";

export function ProductDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();

  const [product, setProduct] = useState<Product | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [qty, setQty] = useState(1);
  const [added, setAdded] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.products
      .get(slug)
      .then(setProduct)
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="mx-auto flex min-h-screen max-w-app flex-col items-center justify-center gap-4 bg-cream px-6 text-center">
        <p className="text-sm text-stone-700">This item is no longer available.</p>
        <Link to="/" className="text-sm font-semibold text-ink underline">
          Back to shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="mx-auto min-h-screen max-w-app animate-pulse bg-cream" />;
  }

  const images = product.images.length > 0 ? product.images : [{ id: "placeholder", url: "", alt: product.name, sortOrder: 0, width: null, height: null }];
  const depositNote =
    product.requiresDeposit &&
    `A ${product.depositPercent ?? 50}% deposit is required to place this order — the balance is settled on delivery.`;

  return (
    <div className="mx-auto flex min-h-screen max-w-app flex-col bg-cream pb-28 sm:my-6 sm:min-h-[calc(100vh-3rem)] sm:rounded-3xl sm:border sm:border-stone-300 sm:shadow-raised">
      <div className="sticky top-0 z-20 flex items-center justify-between bg-cream/95 px-5 py-5 backdrop-blur">
        <button type="button" onClick={() => navigate(-1)} aria-label="Back" className="text-ink">
          <ChevronLeftIcon />
        </button>
        <button
          type="button"
          aria-label={has(product.id) ? "Remove from wishlist" : "Add to wishlist"}
          className={has(product.id) ? "text-wine" : "text-ink"}
          onClick={() => toggle(product.id)}
        >
          <HeartIcon filled={has(product.id)} />
        </button>
      </div>

      <div className="aspect-square w-full overflow-hidden bg-stone-100">
        {images[activeImage]?.url ? (
          <img src={images[activeImage].url} alt={images[activeImage].alt || product.name} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <span className="font-display text-lg text-stone-500">Allthingsnew</span>
          </div>
        )}
      </div>

      {images.length > 1 && (
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-5 py-3">
          {images.map((img, i) => (
            <button
              key={img.id}
              onClick={() => setActiveImage(i)}
              className={`h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === activeImage ? "border-ink" : "border-transparent"}`}
            >
              {img.url && <img src={img.url} alt={img.alt} className="h-full w-full object-cover" />}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-col gap-3 px-5 pt-4">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">Allthingsnew</span>
        <h1 className="font-display text-2xl font-medium leading-snug text-ink">{product.name}</h1>
        <div className="flex items-center gap-3">
          <span className="text-xl font-semibold text-ink">{formatMoney(product.priceCents, product.currency)}</span>
          {product.compareAtPriceCents && (
            <span className="text-sm text-stone-500 line-through">
              {formatMoney(product.compareAtPriceCents, product.currency)}
            </span>
          )}
        </div>

        {product.stockQty <= 5 && product.stockQty > 0 && (
          <span className="w-fit rounded bg-wine/10 px-2 py-1 text-xs font-semibold text-wine">
            Only {product.stockQty} left
          </span>
        )}
        {product.stockQty === 0 && (
          <span className="w-fit rounded bg-stone-100 px-2 py-1 text-xs font-semibold text-stone-700">Sold out</span>
        )}

        {depositNote && (
          <p className="rounded-lg bg-stone-100 px-3 py-2.5 text-xs leading-relaxed text-stone-700">{depositNote}</p>
        )}

        <p className="whitespace-pre-line pt-1 text-sm leading-relaxed text-stone-700">{product.description}</p>

        <div className="flex items-center gap-4 pt-2">
          <span className="text-sm font-semibold text-ink">Quantity</span>
          <div className="flex items-center gap-4 rounded-pill border border-stone-300 px-3 py-2">
            <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} aria-label="Decrease quantity">
              <MinusIcon />
            </button>
            <span className="w-4 text-center text-sm font-semibold">{qty}</span>
            <button
              type="button"
              onClick={() => setQty((q) => Math.min(product.stockQty, q + 1))}
              aria-label="Increase quantity"
              disabled={qty >= product.stockQty}
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 mx-auto flex max-w-app items-center gap-3 border-t border-stone-300 bg-white px-5 py-4 sm:absolute">
        <Button
          fullWidth
          disabled={product.stockQty === 0}
          onClick={() => {
            addItem(product, qty);
            setAdded(true);
            setTimeout(() => setAdded(false), 1500);
          }}
        >
          {added ? "Added ✓" : product.stockQty === 0 ? "Sold Out" : "Add to Bag"}
        </Button>
      </div>
    </div>
  );
}
